import './styles.scss';
import { InfoHeader } from './components/InfoHeader';
import { QuestionsList } from './components/QuestionsList';
import { useMemo, useEffect } from 'react';
import { useListJobsBySurveyQuery } from '../../api/ai-supervisor';
import { Loader } from '../../atoms/loader';
import { useBatchSSE } from './hooks/useBatchSSE';
import { BreadcrumbBar } from './components/BreadcrumbBar';

interface AIAutoAnswerSupervisorProps {
  batchId?: string;
  initiativeId: string;
  surveyId: string;
  handleNavigate: (page: string, extras: { [key: string]: string }) => void;
}

export const AIAutoAnswerSupervisor = ({ batchId: urlBatchId, initiativeId, surveyId, handleNavigate }: AIAutoAnswerSupervisorProps) => {

  // Get all batches for this survey
  const { data: jobsData, isLoading, error: jobsError } = useListJobsBySurveyQuery(
    {
      initiativeId,
      surveyId,
    },
  );

  // Determine selected batch from URL or default to latest
  const selectedBatchId = useMemo(() => {
    if (urlBatchId) return urlBatchId;
    return jobsData?.jobs?.[0]?.batchId;
  }, [urlBatchId, jobsData]);

  // Update URL when auto-selecting latest (backward compatibility)
  useEffect(() => {
    if (!urlBatchId && selectedBatchId && jobsData?.jobs?.length) {
      handleNavigate('auto-answer', { batchId: selectedBatchId });
    }
  }, [urlBatchId, selectedBatchId, initiativeId, surveyId, jobsData, handleNavigate]);

  // Handle batch change from dropdown
  const handleBatchChange = (newBatchId: string) => {
    handleNavigate('auto-answer', { batchId: newBatchId });
  };

  // Single SSE connection for the entire feature - share data with child components
  const sseData = useBatchSSE({
    initiativeId,
    surveyId,
    batchId: selectedBatchId,
    enabled: !!selectedBatchId,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (jobsError) {
    return (
      <>
        <BreadcrumbBar jobsData={jobsData} selectedBatchId={selectedBatchId} onBatchChange={handleBatchChange} />
        <div className='ai-auto-answer-supervisor whiteBoxContainer p-4'>
          <div className='alert alert-danger'>
            <i className='fal fa-exclamation-triangle me-2' />
            Failed to load batch information. Please refresh the page to try again.
          </div>
        </div>
      </>
    );
  }

  if (!selectedBatchId) {
    return (
      <>
        <BreadcrumbBar jobsData={jobsData} selectedBatchId={selectedBatchId} onBatchChange={handleBatchChange} />
        <div className='ai-auto-answer-supervisor whiteBoxContainer p-4'>
          <div className='text-center py-5'>
            <i className='fal fa-inbox fa-3x text-muted mb-3 d-block' />
            <h3>No active Auto-Answer process</h3>
            <p className='text-muted'>Start a new auto-answer process to see progress here.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BreadcrumbBar jobsData={jobsData} selectedBatchId={selectedBatchId} onBatchChange={handleBatchChange} />
      <InfoHeader batchId={selectedBatchId} sseData={sseData} />
      <QuestionsList batchId={selectedBatchId} questionStates={sseData.questions} documentMetadata={sseData.documentMetadata} />
    </>
  );
};
